import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Backdrop, CircularProgress } from '@mui/material';
import { api } from '../api/client.js';

const LoadingContext = createContext(null);

export function LoadingProvider({ children }) {
  const [pending, setPending] = useState(0);
  const show = useCallback(() => setPending((count) => count + 1), []);
  const hide = useCallback(() => setPending((count) => Math.max(0, count - 1)), []);

  useEffect(() => {
    const requestId = api.interceptors.request.use((config) => {
      if (!config.skipLoader) show();
      return config;
    });
    const responseId = api.interceptors.response.use(
      (response) => {
        if (!response.config?.skipLoader) hide();
        return response;
      },
      (error) => {
        if (!error.config?.skipLoader) hide();
        return Promise.reject(error);
      }
    );
    return () => {
      api.interceptors.request.eject(requestId);
      api.interceptors.response.eject(responseId);
    };
  }, [hide, show]);

  const value = useMemo(() => ({ show, hide, isLoading: pending > 0 }), [hide, pending, show]);

  return (
    <LoadingContext.Provider value={value}>
      {children}
      <Backdrop open={pending > 0} sx={{ zIndex: (theme) => theme.zIndex.modal + 2, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  const context = useContext(LoadingContext);
  if (!context) throw new Error('useLoading must be used within LoadingProvider');
  return context;
}
